import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  getAllTeamsMeetings,
  getUpcomingWeekMeetings,
  getTeamsMeetingById,
  createTeamsMeetingInvite,
  initiateTeamsLogin,
  handleOAuthCallback,
  getTeamsAuthStatus,
  TeamsMeetingResponse,
  TeamsMeetingInvitePayload,
  TeamsLoginResponse,
  TeamsStatusResponse
} from '../../services/TeamServices/teamService';
import { Api } from '../api/axiosInstance';

export interface InterviewData {
  candidate_interview_info_id: number;
  candidate_id: number;
  InterviewDate: string;
  interview_status: string | null;
  offer_status: string | null;
  position: string | null;
  current_title: string | null;
  current_org: string | null;
  total_experience: number;
  relevant_experience: number;
  current_salary: number;
  expected_salary: number;
  notice_period: number;
  feedback: string | null;
  interview_type: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  candidate_status: string;
  resume: string | null;
  job_id: number | null;
  job_title: string | null;
  company_name: string | null;
}

interface TeamsMeetingsState {
  meetings: TeamsMeetingResponse[];
  upcomingWeekMeetings: TeamsMeetingResponse[];
  selectedMeeting: TeamsMeetingResponse | null;
  authStatus: TeamsStatusResponse | null;
  loginResponse: TeamsLoginResponse | null;
  loading: boolean;
  upcomingWeekLoading: boolean;
  meetingDetailsLoading: boolean;
  authLoading: boolean;
  createMeetingLoading: boolean;
  error: string | null;
  upcomingWeekError: string | null;
  meetingDetailsError: string | null;
  authError: string | null;
  createMeetingError: string | null;
  success: boolean;
  createMeetingSuccess: boolean;
  localInterviews: InterviewData[];
  localInterviewsLoading: boolean;
  localInterviewsError: string | null;
}

const initialState: TeamsMeetingsState = {
  meetings: [],
  upcomingWeekMeetings: [],
  selectedMeeting: null,
  authStatus: null,
  loginResponse: null,
  loading: false,
  upcomingWeekLoading: false,
  meetingDetailsLoading: false,
  authLoading: false,
  createMeetingLoading: false,
  error: null,
  upcomingWeekError: null,
  meetingDetailsError: null,
  authError: null,
  createMeetingError: null,
  success: false,
  createMeetingSuccess: false,
  localInterviews: [],
  localInterviewsLoading: false,
  localInterviewsError: null,
};

// Async Thunks
export const fetchAllTeamsMeetings = createAsyncThunk<
  TeamsMeetingResponse[],
  void,
  { rejectValue: string }
>(
  'teamsMeetings/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllTeamsMeetings();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch Teams meetings');
    }
  }
);

export const fetchUpcomingWeekMeetings = createAsyncThunk<
  TeamsMeetingResponse[],
  void,
  { rejectValue: string }
>(
  'teamsMeetings/fetchUpcomingWeek',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUpcomingWeekMeetings();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch upcoming week meetings');
    }
  }
);

export const fetchTeamsMeetingById = createAsyncThunk<
  TeamsMeetingResponse,
  string,
  { rejectValue: string }
>(
  'teamsMeetings/fetchById',
  async (meetingId, { rejectWithValue }) => {
    try {
      const response = await getTeamsMeetingById(meetingId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch meeting details');
    }
  }
);

export const createMeetingInvite = createAsyncThunk<
  any,
  TeamsMeetingInvitePayload,
  { rejectValue: string }
>(
  'teamsMeetings/createInvite',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createTeamsMeetingInvite(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create meeting invite');
    }
  }
);

export const initializeTeamsLogin = createAsyncThunk<
  TeamsLoginResponse,
  void,
  { rejectValue: string }
>(
  'teamsMeetings/initiateLogin',
  async (_, { rejectWithValue }) => {
    try {
      const response = await initiateTeamsLogin();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to initiate Teams login');
    }
  }
);

export const processOAuthCallback = createAsyncThunk<
  any,
  { code: string; state: string },
  { rejectValue: string }
>(
  'teamsMeetings/oauthCallback',
  async ({ code, state }, { rejectWithValue }) => {
    try {
      const response = await handleOAuthCallback(code, state);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to process OAuth callback');
    }
  }
);

export const fetchTeamsAuthStatus = createAsyncThunk<
  TeamsStatusResponse,
  void,
  { rejectValue: string }
>(
  'teamsMeetings/fetchAuthStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getTeamsAuthStatus();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch auth status');
    }
  }
);

export const fetchAllInterviews = createAsyncThunk<
  InterviewData[],
  void,
  { rejectValue: string }
>(
  'teamsMeetings/fetchAllInterviews',
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.get('/interviews');
      // Response shape: { data: { success, data: [...] } }
      const payload = response.data?.data;
      return Array.isArray(payload) ? payload : (payload?.data || []);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch interviews');
    }
  }
);

// Slice
const teamsMeetingsSlice = createSlice({
  name: 'teamsMeetings',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.upcomingWeekError = null;
      state.meetingDetailsError = null;
      state.authError = null;
      state.createMeetingError = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.createMeetingSuccess = false;
    },
    clearSelectedMeeting: (state) => {
      state.selectedMeeting = null;
    },
    clearLoginResponse: (state) => {
      state.loginResponse = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch All Meetings
    builder
      .addCase(fetchAllTeamsMeetings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTeamsMeetings.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload;
        state.success = true;
      })
      .addCase(fetchAllTeamsMeetings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch meetings';
      });

    // Fetch Upcoming Week Meetings
    builder
      .addCase(fetchUpcomingWeekMeetings.pending, (state) => {
        state.upcomingWeekLoading = true;
        state.upcomingWeekError = null;
      })
      .addCase(fetchUpcomingWeekMeetings.fulfilled, (state, action) => {
        state.upcomingWeekLoading = false;
        state.upcomingWeekMeetings = action.payload;
        state.success = true;
      })
      .addCase(fetchUpcomingWeekMeetings.rejected, (state, action) => {
        state.upcomingWeekLoading = false;
        state.upcomingWeekError = action.payload || 'Failed to fetch upcoming week meetings';
      });

    // Fetch Meeting by ID
    builder
      .addCase(fetchTeamsMeetingById.pending, (state) => {
        state.meetingDetailsLoading = true;
        state.meetingDetailsError = null;
      })
      .addCase(fetchTeamsMeetingById.fulfilled, (state, action) => {
        state.meetingDetailsLoading = false;
        state.selectedMeeting = action.payload;
        state.success = true;
      })
      .addCase(fetchTeamsMeetingById.rejected, (state, action) => {
        state.meetingDetailsLoading = false;
        state.meetingDetailsError = action.payload || 'Failed to fetch meeting details';
      });

    // Create Meeting Invite
    builder
      .addCase(createMeetingInvite.pending, (state) => {
        state.createMeetingLoading = true;
        state.createMeetingError = null;
        state.createMeetingSuccess = false;
      })
      .addCase(createMeetingInvite.fulfilled, (state, action) => {
        state.createMeetingLoading = false;
        state.createMeetingSuccess = true;
      })
      .addCase(createMeetingInvite.rejected, (state, action) => {
        state.createMeetingLoading = false;
        state.createMeetingError = action.payload || 'Failed to create meeting invite';
      });

    // Initiate Teams Login
    builder
      .addCase(initializeTeamsLogin.pending, (state) => {
        state.authLoading = true;
        state.authError = null;
      })
      .addCase(initializeTeamsLogin.fulfilled, (state, action) => {
        state.authLoading = false;
        state.loginResponse = action.payload;
      })
      .addCase(initializeTeamsLogin.rejected, (state, action) => {
        state.authLoading = false;
        state.authError = action.payload || 'Failed to initiate Teams login';
      });

    // Process OAuth Callback
    builder
      .addCase(processOAuthCallback.pending, (state) => {
        state.authLoading = true;
        state.authError = null;
      })
      .addCase(processOAuthCallback.fulfilled, (state, action) => {
        state.authLoading = false;
        state.success = true;
      })
      .addCase(processOAuthCallback.rejected, (state, action) => {
        state.authLoading = false;
        state.authError = action.payload || 'Failed to process OAuth callback';
      });

    // Fetch Auth Status
    builder
      .addCase(fetchTeamsAuthStatus.pending, (state) => {
        state.authLoading = true;
        state.authError = null;
      })
      .addCase(fetchTeamsAuthStatus.fulfilled, (state, action) => {
        state.authLoading = false;
        state.authStatus = action.payload;
      })
      .addCase(fetchTeamsAuthStatus.rejected, (state, action) => {
        state.authLoading = false;
        state.authError = action.payload || 'Failed to fetch auth status';
      });

    // Fetch All Local Interviews
    builder
      .addCase(fetchAllInterviews.pending, (state) => {
        state.localInterviewsLoading = true;
        state.localInterviewsError = null;
      })
      .addCase(fetchAllInterviews.fulfilled, (state, action) => {
        state.localInterviewsLoading = false;
        state.localInterviews = action.payload;
      })
      .addCase(fetchAllInterviews.rejected, (state, action) => {
        state.localInterviewsLoading = false;
        state.localInterviewsError = action.payload || 'Failed to fetch interviews';
      });
  },
});

export const { clearErrors, clearSuccess, clearSelectedMeeting, clearLoginResponse } = teamsMeetingsSlice.actions;

export default teamsMeetingsSlice.reducer;